// import PrivacyPolicyLink from "@/ui/components/PrivacyPolicyLink";
// import { Lock } from "lucide-preact";
import { useState, useEffect, useRef } from "preact/hooks";
import { Button } from "@/ui/shadcn/button";
import { isDevHost, isDesktop } from "@/lib/web";
import { uiLangIsRtl } from "@/l11n/l11n";
import { doLog } from "@/lib/globals";
import { validateEmail } from "@/lib/email_validator";

const COMMON_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com"];

export default function EmailInput({ strings, onSubmit, onFocus, onBlur }) {
  // MARK: State
  const [value, setValue] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const inputRef = useRef();

  const isRtl = uiLangIsRtl();

  // MARK: Dev Mode - pre-fill email

  const setRandomEmail = () => {
    if (isDevHost) {
      // In dev mode, set a random email to test the email input
      if (!value) {
        const randomString = Math.random().toString(36).substring(2, 8);
        setValue(`purnellfisher+${randomString}@gmail.com`);
      }
    } else {
      // Focus the input
      if (isDesktop) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 200);
      }
    }
  };

  useEffect(() => {
    setRandomEmail();
  }, []);

  // MARK: Validate
  useEffect(() => {
    const valid = validateEmail(value);
    setIsValid(valid);
    const parts = value.split("@");
    if (parts[1]) {
      setSuggestions(
        COMMON_DOMAINS.filter((d) => d.startsWith(parts[1]) && d !== parts[1]).map(
          (d) => `${parts[0]}@${d}`
        )
      );
    } else {
      setSuggestions([]);
    }
  }, [value]);

  // MARK: Handle Input
  const handleChange = (e) => {
    let v = e.currentTarget.value;
    setValue(v);
  };

  // MARK: Submit
  const handleSend = (e) => {
    e.preventDefault();
    if (doLog()) console.log("handleSend raw value:", value);
    if (isValid && value.trim()) {
      if (doLog()) console.log("handleSend final value:", value);
      onSubmit({
        value,
      });
      setValue(""); // Clear the input after submitting
    }
  };

  // MARK: Suggestions
  const handleSuggestionClick = (s) => {
    setValue(s);
    setSuggestions([]);
    inputRef.current.focus();
  };

  // MARK: Clear
  const handleClear = () => {
    setValue("");
    setIsValid(false);
    setSuggestions([]);
    inputRef.current.focus();
  };

  // MARK: Render
  return (
    <form onSubmit={handleSend} className="w-full">
      <div className="flex items-center gap-2">
        {/* Text Input Field */}
        <div className="flex-1 relative">
          <input
            key="email_input"
            ref={inputRef}
            type="email"
            value={value}
            placeholder={strings.input_placeholder_email}
            onInput={handleChange}
            onFocus={() => {
              if (doLog()) console.log("onFocus EmailInput");
              if (onFocus) onFocus();
            }}
            onBlur={() => {
              if (doLog()) console.log("onBlur EmailInput");
              if (onBlur) onBlur();
            }}
            onTouchMove={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                handleSend(e);
              }
            }}
            aria-invalid={value ? !isValid : undefined}
            style={{
              width: "100%",
              padding: "0.5rem",
              border: `1px solid ${!value ? "#ccc" : isValid ? "green" : "red"}`,
              borderRadius: "4px",
              fontSize: "1rem",
              height: "40px",
              boxSizing: "border-box",
            }}
            className={`w-full border-none outline-none ring-0 focus:ring-0 focus:border-none focus:outline-none focus-visible:ring-0 focus-visible:border-none focus-visible:outline-none px-2 py-2 text-lg bg-transparent shadow-none ${
              isRtl ? "text-right" : "text-left"
            }`}
          />

          {value && !value.startsWith("+") && (
            <button
              onClick={handleClear}
              style={{
                position: "absolute",
                [isRtl ? "left" : "right"]: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                fontSize: "1.2rem",
                cursor: "pointer",
                lineHeight: 1,
                zIndex: 1,
                padding: 0,
              }}
              aria-label="Clear"
            >
              ×
            </button>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="taalia_std"
          disabled={!isValid || !value.trim()}
          style={{
            height: "40px",
            boxSizing: "border-box",
          }}
        >
          {strings.submit}
        </Button>
      </div>

      {/* Unlock Your Free Assessment */}
      {/* <div className="flex items-center justify-center text-gray-700 px-2 pt-2">
        <div className="text-sm font-semibold">{strings.unlock_your_free_assessment}</div>
      </div> */}

      {/* Security Notice */}
      {/* <div
        className="flex items-start justify-center text-gray-700 px-2 pt-1"
        style={{ fontSize: "0.65rem", lineHeight: "0.75rem" }}
      >
        <Lock className="w-6 h-6 text-gray-500 mr-2" />
        <div className="mt-1">
          {strings.email_phone_input_detail}{" "}
          <div className="mt-1">
            <PrivacyPolicyLink title={strings.privacy_policy} showPrivacyPolicy={true} />
          </div>
        </div>
        <Lock className="w-6 h-6 text-gray-500 ml-2" />
      </div> */}

      {suggestions.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            margin: 0,
            padding: "0.25rem 0",
            listStyle: "none",
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            maxHeight: "8rem",
            overflowY: "auto",
            zIndex: 1000,
          }}
        >
          {suggestions.map((s) => (
            <li
              key={s}
              onMouseDown={() => handleSuggestionClick(s)}
              style={{ padding: "0.5rem", cursor: "pointer" }}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}
